import { Injectable } from '@angular/core';
import { HttpStatusEnum, UserApi } from '@core/api';
import { UserRoleEnum } from '@shared/enums';
import { CurrentUser } from '@shared/interfaces';
import { BehaviorSubject, filter, firstValueFrom, Subject } from 'rxjs';
import { UserPreferencesService } from './user-preferences.service';

@Injectable({ providedIn: 'root' })
export class CurrentUserService {
    readonly currentUser$ = new BehaviorSubject<CurrentUser | null>(null);
    currentUserError$: Subject<boolean> = new Subject<boolean>();
    forbiddenAccessToResource$ = new Subject<void>();

    private loadingCurrentUserDetails$ = new BehaviorSubject<'loading' | 'ready'>('ready');

    constructor(
        private userApi: UserApi,
        private userPreferencesService: UserPreferencesService
    ) {}

    async initializeCurrentUser(): Promise<CurrentUser | null> {
        if (this.currentUser$.value) return this.currentUser$.value;

        if (this.loadingCurrentUserDetails$.value === 'loading') {
            await firstValueFrom(this.loadingCurrentUserDetails$.pipe(filter(state => state === 'ready')));
            return this.currentUser$.value;
        }

        this.loadingCurrentUserDetails$.next('loading');
        const response = await this.userApi.getUserAndPreferences();
        if (response.status === HttpStatusEnum.SUCCESS && response.data) {
            this.userPreferencesService.initializeUserPreferences(response.data.preferences);
            this.setCurrentUser(response.data.user);
        }

        this.loadingCurrentUserDetails$.next('ready');
        return this.currentUser$.value;
    }

    getCurrentUser(): CurrentUser | null {
        return this.currentUser$.value;
    }

    getCurrentUserRole(): UserRoleEnum {
        return this.currentUser$.value?.role ?? UserRoleEnum.UNKNOWN;
    }

    setCurrentUser(user: CurrentUser): void {
        this.currentUser$.next(user);
    }

    triggerUserDetailsError(hasError: boolean): void {
        this.currentUserError$.next(hasError);
    }

    triggerForbiddenAccessToResourceError(): void {
        this.forbiddenAccessToResource$.next();
    }
}
